<script>
import { ref, computed, provide } from 'vue';
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import * as z from 'zod';
import Loading from '@shell/components/Loading';
import CreateEditView from '@shell/mixins/create-edit-view';
import AuthConfigIdentity from '@shell/components/auth/AuthConfigIdentity.vue';
import AuthConfig, { SLO_OPTION_VALUES } from '@shell/mixins/auth-config';
import CruResource from '@shell/components/CruResource';
import { LabeledInput } from '@components/Form/LabeledInput';
import { Checkbox } from '@components/Form/Checkbox';
import LabeledSelect from '@shell/components/form/LabeledSelect';
import { Banner } from '@components/Banner';
import AllowedPrincipals from '@shell/components/auth/AllowedPrincipals';
import FileSelectorTextArea from '@shell/components/form/FileSelectorTextArea.vue';
import AuthBanner from '@shell/components/auth/AuthBanner';
import config, { ADFS, OKTA, SHIBBOLETH } from '@shell/edit/auth/ldap/config';
import AuthProviderWarningBanners from '@shell/edit/auth/AuthProviderWarningBanners';
import RadioGroup from '@components/Form/Radio/RadioGroup.vue';
import { RcButton } from '@components/RcButton';
import { useI18n } from '@shell/composables/useI18n';
import { RcSeparator } from '@components/RcSeparator';
import { zodValidators } from '@shell/utils/validators/zod-helpers';
import { isRancherPrime } from '@shell/config/version';
import { ADFS_LDAP_SEARCH } from '@shell/store/features';

// Standard LDAP defaults
const LDAP_DEFAULTS = {
  connectionTimeout:            5000,
  groupDNAttribute:             'entryDN',
  groupMemberMappingAttribute:  'member',
  groupMemberUserAttribute:     'entryDN',
  groupNameAttribute:           'cn',
  groupObjectClass:             'groupOfNames',
  groupSearchAttribute:         'cn',
  nestedGroupMembershipEnabled: false,
  port:                         389,
  servers:                      [],
  starttls:                     false,
  tls:                          false,
  disabledStatusBitmask:        0,
  userLoginAttribute:           'uid',
  userMemberAttribute:          'memberOf',
  userNameAttribute:            'cn',
  userObjectClass:              'inetOrgPerson',
  userSearchAttribute:          'uid|sn|givenName'
};

const GENERIC_SAML = 'genericsaml';

// Providers that expose the Entity ID field
const ENTITY_ID_PROVIDERS = ['keycloak', 'ping', GENERIC_SAML];

// Generic SAML NameID formats. Labels are translated, values mirror the backend enum.
const NAME_ID_FORMATS = ['unspecified', 'emailAddress', 'transient', 'persistent'];

// Generic SAML signature algorithms. These are standard algorithm identifiers, so they aren't translated.
const SIGNATURE_METHODS = [
  { value: 'RSA-SHA256', label: 'RSA-SHA256' },
  { value: 'RSA-SHA1', label: 'RSA-SHA1' },
  { value: 'RSA-SHA512', label: 'RSA-SHA512' },
];

export default {
  components: {
    AuthConfigIdentity,
    Loading,
    CruResource,
    LabeledInput,
    LabeledSelect,
    Banner,
    AllowedPrincipals,
    Checkbox,
    RadioGroup,
    FileSelectorTextArea,
    config,
    AuthBanner,
    AuthProviderWarningBanners,
    RcButton,
    RcSeparator,
  },

  mixins: [CreateEditView, AuthConfig],

  setup() {
    const store = useStore();
    const { t } = useI18n(store);
    const { field } = zodValidators(t);

    const ldapEnabledRef = ref(false);
    const ldapTlsEnabledRef = ref(false);

    const ldapSchema = () => ({
      hostname:                        field('authConfig.ldap.hostname.label').required(),
      port:                            field('authConfig.ldap.port').required(),
      certificate:                     ldapTlsEnabledRef.value ? field('authConfig.ldap.cert').required() : field(),
      connectionTimeout:               field('authConfig.ldap.serverConnectionTimeout').required(),
      serviceAccountDistinguishedName: field('authConfig.ldap.serviceAccountDN').required(),
      serviceAccountPassword:          field('authConfig.ldap.serviceAccountPassword').required(),
      userSearchBase:                  field('authConfig.ldap.userSearchBase.label').required(),
    });

    const validationSchema = computed(() => toTypedSchema(
      z.object({
        displayNameField:   field('authConfig.saml.displayName').required(),
        userNameField:      field('authConfig.saml.userName').required(),
        uidField:           field('authConfig.saml.UID').required(),
        groupsField:        field('authConfig.saml.groups').required(),
        rancherApiHost:     field('authConfig.saml.api').url().required(),
        spKey:              field('authConfig.saml.key.label').required(),
        spCert:             field('authConfig.saml.cert.label').required(),
        idpMetadataContent: field('authConfig.saml.metadata.label').required(),
        ...(ldapEnabledRef.value ? ldapSchema() : {}),
      })
    ));

    const showAllErrors = ref(false);

    provide('vee-show-all-errors', showAllErrors);

    const { errors, validate } = useForm({ validationSchema });
    const isFormValid = computed(() => Object.keys(errors.value).length === 0);

    const validateAllFields = async() => {
      await validate();
      showAllErrors.value = true;
    };

    return {
      isFormValid, validateAllFields, ldapEnabledRef, ldapTlsEnabledRef
    };
  },

  data() {
    return {
      showLdap:        false,
      showLdapDetails: false,
    };
  },

  created() {
    this.registerBeforeHook(this.validateAllFields, 'willSave');
  },

  computed: {
    isGenericSaml() {
      return this.NAME === GENERIC_SAML;
    },

    supportsEntityId() {
      return ENTITY_ID_PROVIDERS.includes(this.NAME);
    },

    nameIDFormatOptions() {
      return NAME_ID_FORMATS.map((value) => ({ value, label: this.t(`authConfig.saml.nameIDFormatOptions.${ value }`) }));
    },

    signatureMethodOptions() {
      return SIGNATURE_METHODS;
    },

    nameIDFormatLabel() {
      return this.nameIDFormatOptions.find((option) => option.value === this.model?.nameIDFormat)?.label || '';
    },

    validationPassed() {
      if (this.model?.enabled && !this.editConfig) {
        return true;
      }

      return this.isFormValid;
    },

    tArgs() {
      return {
        baseUrl:  this.serverSetting,
        provider: this.displayName,
        username: this.principal.loginName || this.principal.name,
      };
    },

    isLogoutAllSupported() {
      return this.model?.logoutAllSupported;
    },

    sloOptions() {
      return [
        { value: SLO_OPTION_VALUES.rancher, label: this.t('authConfig.slo.sloOptions.onlyRancher', { name: this.model?.nameDisplay }) },
        { value: SLO_OPTION_VALUES.all, label: this.t('authConfig.slo.sloOptions.logoutAll', { name: this.model?.nameDisplay }) },
        { value: SLO_OPTION_VALUES.both, label: this.t('authConfig.slo.sloOptions.choose') },
      ];
    },

    sloTypeText() {
      const sloOptionSelected = this.sloOptions.find((item) => item.value === this.sloType);

      return sloOptionSelected?.label || '';
    },

    toSave() {
      return { enabled: true, ...this.model };
    },

    // Does the auth provider support LDAP for search in addition to SAML?
    supportsLDAPSearch() {
      if (this.NAME === ADFS) {
        return isRancherPrime() && this.$store.getters['features/get'](ADFS_LDAP_SEARCH);
      }

      return this.NAME === SHIBBOLETH || this.NAME === OKTA;
    },

    ldapHosts() {
      const hosts = this.model?.openLdapConfig.servers || [];

      return hosts.join(',');
    },

    ldapFieldsShown() {
      return !!(this.supportsLDAPSearch && this.showLdap && this.model?.openLdapConfig);
    },

    ldapTlsEnabled() {
      return !!(this.model?.openLdapConfig?.tls || this.model?.openLdapConfig?.starttls);
    },

    ldapProtocol() {
      if (this.model?.openLdapConfig?.starttls) {
        return this.t('authConfig.ldap.protocols.starttls');
      } else if (this.model?.openLdapConfig?.tls) {
        return this.t('authConfig.ldap.protocols.tls');
      }

      return this.t('authConfig.ldap.protocols.ldap');
    }
  },
  watch: {
    showLdap(neu, old) {
      if (neu && !this.model.openLdapConfig) {
        // Use a spread of config, so that if don't make changes to the defaults if the user edits them
        this.model['openLdapConfig'] = { ...LDAP_DEFAULTS };
      }
    },
    ldapFieldsShown: {
      handler(neu) {
        this.ldapEnabledRef = neu;
      },
      immediate: true
    },
    ldapTlsEnabled: {
      handler(neu) {
        this.ldapTlsEnabledRef = neu;
      },
      immediate: true
    },
    // sloType is defined on shell/mixins/auth-config.js
    sloType(neu) {
      switch (neu) {
      case SLO_OPTION_VALUES.rancher:
        this.model.logoutAllEnabled = false;
        this.model.logoutAllForced = false;
        break;
      case SLO_OPTION_VALUES.all:
        this.model.logoutAllEnabled = true;
        this.model.logoutAllForced = true;
        break;
      case SLO_OPTION_VALUES.both:
        this.model.logoutAllEnabled = true;
        this.model.logoutAllForced = false;
        break;
      }
    }
  },
};
</script>

<template>
  <Loading v-if="$fetchState.pending" />
  <div v-else>
    <CruResource
      :cancel-event="true"
      :done-route="doneRoute"
      :mode="mode"
      :resource="model"
      :subtypes="[]"
      :validation-passed="validationPassed"
      :finish-button-mode="model.enabled ? 'edit' : 'enable'"
      :can-yaml="false"
      :errors="errors"
      :show-cancel="showCancel"
      @error="e=>errors = e"
      @finish="save"
      @cancel="cancel"
    >
      <AuthConfigIdentity
        v-model:name="configName"
        v-model:description="configDescription"
        :name-fixed="configNameFixed"
        :name-error="configNameError"
      />

      <template v-if="model.enabled && !isEnabling && !editConfig">
        <AuthBanner
          :t-args="tArgs"
          :name="authConfigDisplayName"
          :disable="disable"
          :edit="goToEdit"
          :provider-id="model.id"
        >
          <template #rows>
            <tr><td>{{ t(`authConfig.saml.displayName`) }}: </td><td>{{ model.displayNameField }}</td></tr>
            <tr><td>{{ t(`authConfig.saml.userName`) }}: </td><td>{{ model.userNameField }}</td></tr>
            <tr><td>{{ t(`authConfig.saml.UID`) }}: </td><td>{{ model.uidField }}</td></tr>
            <tr><td>{{ t(`authConfig.saml.entityID`) }}: </td><td>{{ model.entityID }}</td></tr>
            <tr><td>{{ t(`authConfig.saml.api`) }}: </td><td>{{ model.rancherApiHost }}</td></tr>
            <tr><td>{{ t(`authConfig.saml.groups`) }}: </td><td>{{ model.groupsField }}</td></tr>
            <template v-if="isGenericSaml">
              <tr v-if="model.nameIDFormat">
                <td>{{ t(`authConfig.saml.nameIDFormat`) }}: </td><td>{{ nameIDFormatLabel }}</td>
              </tr>
              <tr v-if="model.signatureMethod">
                <td>{{ t(`authConfig.saml.signatureMethod`) }}: </td><td>{{ model.signatureMethod }}</td>
              </tr>
              <tr data-testid="genericsaml-view-fields">
                <td>{{ t(`authConfig.saml.allowIdpInitiated`) }}: </td><td>{{ model.allowIdpInitiated ? t('generic.enabled') : t('generic.disabled') }}</td>
              </tr>
              <tr><td>{{ t(`authConfig.saml.forceAuthn`) }}: </td><td>{{ model.forceAuthn ? t('generic.enabled') : t('generic.disabled') }}</td></tr>
            </template>
            <tr v-if="isLogoutAllSupported">
              <td>{{ t(`authConfig.slo.sloTitle`) }}: </td><td>{{ sloTypeText }}</td>
            </tr>
          </template>

          <template
            v-if="supportsLDAPSearch"
            #footer
          >
            <Banner
              v-if="showLdap"
              color="success"
              class="banner"
            >
              <div
                class="advanced-ldap-banner"
              >
                <div>{{ t('authConfig.saml.search.on') }}</div>
                <div>
                  <rc-button
                    variant="link"
                    @click="showLdapDetails = !showLdapDetails"
                  >
                    <template v-if="showLdapDetails">
                      {{ t('authConfig.saml.search.hide') }}
                    </template>
                    <template v-else>
                      {{ t('authConfig.saml.search.show') }}
                    </template>
                  </rc-button>
                </div>
              </div>
            </Banner>
            <Banner
              v-else
              color="info"
            >
              {{ t('authConfig.saml.search.off') }}
            </Banner>

            <table v-if="showLdapDetails && model.openLdapConfig">
              <tbody>
                <tr><td>{{ t('authConfig.ldap.hostname.label') }}:</td><td>{{ ldapHosts }}</td></tr>
                <tr><td>{{ t('authConfig.ldap.port') }}:</td><td>{{ model.openLdapConfig.port }}</td></tr>
                <tr><td>{{ t('authConfig.ldap.protocol') }}:</td><td>{{ ldapProtocol }}</td></tr>
                <tr><td>{{ t('authConfig.ldap.serviceAccountDN') }}:</td><td>{{ model.openLdapConfig.serviceAccountDistinguishedName }}</td></tr>
                <tr><td>{{ t('authConfig.ldap.userSearchBase.label') }}:</td><td>{{ model.openLdapConfig.userSearchBase }}</td></tr>
                <tr><td>{{ t('authConfig.ldap.groupSearchBase.label') }}:</td><td>{{ model.openLdapConfig.groupSearchBase }}</td></tr>
                <tr v-if="model.openLdapConfig.userIDAttribute">
                  <td>{{ t('authConfig.ldap.userIDAttribute') }}:</td><td>{{ model.openLdapConfig.userIDAttribute }}</td>
                </tr>
                <tr v-if="model.openLdapConfig.groupIDAttribute">
                  <td>{{ t('authConfig.ldap.groupIDAttribute') }}:</td><td>{{ model.openLdapConfig.groupIDAttribute }}</td>
                </tr>
              </tbody>
            </table>
          </template>
        </AuthBanner>

        <RcSeparator />

        <AllowedPrincipals
          :provider="NAME"
          :auth-config="model"
          :mode="mode"
        />
      </template>

      <template v-else>
        <AuthProviderWarningBanners
          v-if="!model.enabled"
          :t-args="tArgs"
        />

        <h3>{{ t(`authConfig.saml.${NAME}`) }}</h3>

        <div class="row mb-20">
          <div class="col span-6">
            <LabeledInput
              v-model:value="model.displayNameField"
              name="displayNameField"
              :label="t(`authConfig.saml.displayName`)"
              :mode="mode"
              data-testid="saml-display-name-field"
              required
            />
          </div>
          <div class="col span-6">
            <LabeledInput
              v-model:value="model.userNameField"
              name="userNameField"
              :label="t(`authConfig.saml.userName`)"
              :mode="mode"
              data-testid="saml-user-name-field"
              required
            />
          </div>
        </div>

        <div class="row mb-20">
          <div class="col span-6">
            <LabeledInput
              v-model:value="model.uidField"
              name="uidField"
              :label="t(`authConfig.saml.UID`)"
              :mode="mode"
              data-testid="saml-uid-field"
              required
            />
          </div>
          <div class="col span-6">
            <LabeledInput
              v-model:value="model.groupsField"
              name="groupsField"
              :label="t(`authConfig.saml.groups`)"
              :mode="mode"
              data-testid="saml-groups-field"
              required
            />
          </div>
        </div>

        <div class="row mb-20">
          <div
            v-if="supportsEntityId"
            class="col span-6"
          >
            <LabeledInput
              v-model:value="model.entityID"
              :label="t(`authConfig.saml.entityID`)"
              :mode="mode"
              data-testid="saml-entity-id-field"
            />
          </div>
          <div class="col span-6">
            <LabeledInput
              v-model:value="model.rancherApiHost"
              name="rancherApiHost"
              :label="t(`authConfig.saml.api`)"
              :mode="mode"
              data-testid="saml-rancher-api-host"
              required
            />
          </div>
        </div>

        <div class="row mb-20">
          <div class="col span-12">
            <FileSelectorTextArea
              v-model:value="model.spKey"
              class="mb-20"
              name="spKey"
              :label="t(`authConfig.saml.key.label`)"
              :placeholder="t(`authConfig.saml.key.placeholder`)"
              :mode="mode"
              data-testid="saml-key"
              required
            />
            <FileSelectorTextArea
              v-model:value="model.spCert"
              class="mb-20"
              name="spCert"
              :label="t(`authConfig.saml.cert.label`)"
              :placeholder="t(`authConfig.saml.cert.placeholder`)"
              :mode="mode"
              data-testid="saml-cert"
              required
            />
            <FileSelectorTextArea
              v-model:value="model.idpMetadataContent"
              name="idpMetadataContent"
              :label="t(`authConfig.saml.metadata.label`)"
              :placeholder="t(`authConfig.saml.metadata.placeholder`)"
              :mode="mode"
              data-testid="saml-metadata"
              required
            />
          </div>
        </div>

        <!-- Generic SAML options -->
        <template v-if="isGenericSaml">
          <div data-testid="genericsaml-fields">
            <div class="row mb-20">
              <div class="col span-6">
                <LabeledSelect
                  v-model:value="model.nameIDFormat"
                  :label="t('authConfig.saml.nameIDFormat')"
                  :options="nameIDFormatOptions"
                  :mode="mode"
                  data-testid="saml-nameid-format"
                />
              </div>
              <div class="col span-6">
                <LabeledSelect
                  v-model:value="model.signatureMethod"
                  :label="t('authConfig.saml.signatureMethod')"
                  :options="signatureMethodOptions"
                  :mode="mode"
                  data-testid="saml-signature-algorithm"
                />
              </div>
            </div>
            <div class="row mb-10">
              <div class="col span-12">
                <Checkbox
                  v-model:value="model.allowIdpInitiated"
                  :label="t('authConfig.saml.allowIdpInitiated')"
                  :mode="mode"
                  data-testid="saml-allow-idp-initiated"
                />
              </div>
            </div>
            <div class="row mb-20">
              <div class="col span-12">
                <Checkbox
                  v-model:value="model.forceAuthn"
                  :label="t('authConfig.saml.forceAuthn')"
                  :mode="mode"
                  data-testid="saml-force-authn"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- SLO logout -->
        <div
          v-if="isLogoutAllSupported"
          class="mt-10 mb-30"
        >
          <div class="row">
            <div class="col span-12">
              <h3>{{ t('authConfig.slo.sloTitle') }}</h3>
            </div>
          </div>
          <div class="row">
            <div class="col span-4">
              <RadioGroup
                v-model:value="sloType"
                :mode="mode"
                :options="sloOptions"
                :disabled="!model.logoutAllSupported"
                name="sloTypeRadio"
              />
            </div>
          </div>
        </div>

        <!-- LDAP search -->
        <div v-if="supportsLDAPSearch">
          <div class="row">
            <h3>{{ t('authConfig.saml.search.title') }}</h3>
          </div>
          <div class="row">
            <Banner
              label-key="authConfig.saml.search.message"
              color="info"
            />
          </div>
          <div class="row">
            <Checkbox
              v-model:value="showLdap"
              data-testid="saml-show-ldap"
              :mode="mode"
              :label="t('authConfig.saml.showLdap')"
            />
          </div>
          <div class="row mt-20 mb-20">
            <config
              v-if="showLdap && model.openLdapConfig"
              v-model:value="model.openLdapConfig"
              :type="NAME"
              :mode="mode"
              :is-create="!model.enabled"
            />
          </div>
        </div>
      </template>
    </CruResource>
  </div>
</template>
<style lang="scss" scoped>
  .banner {
    display: block;

    &:deep() code {
      padding: 0 3px;
      margin: 0 3px;
    }
  }

  // Banner shows message and link formatted right aligned
  .advanced-ldap-banner {
    display: flex;
    flex: 1;

    > :first-child {
      flex: 1;
    }
  }
</style>
